import { useEffect, useState } from 'react';
import {
  Me,
  Suggestions,
  type SuggestionRow,
  type SuggestionStatus,
  type UserSummary,
} from '../lib/api';
import { Badge } from '../components/Badge';
import { suggestionStatusTone, suggestionStatusLabel } from '../lib/ticket-ui';
import { confirmDialog } from '../components/ui/confirm';

const STATUS_OPTIONS: SuggestionStatus[] = ['OPEN', 'PLANNED', 'DONE', 'DECLINED'];

export function SuggestionsPage() {
  const [items, setItems] = useState<SuggestionRow[]>([]);
  const [me, setMe] = useState<UserSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [creating, setCreating] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [s, m] = await Promise.all([Suggestions.list(), Me.get()]);
      setItems(s.suggestions);
      setMe(m.user);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function create() {
    if (!newTitle.trim() || !newBody.trim() || creating) return;
    setCreating(true);
    try {
      await Suggestions.create({ title: newTitle.trim(), body: newBody.trim() });
      setNewTitle('');
      setNewBody('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit');
    } finally {
      setCreating(false);
    }
  }

  const isAdmin = me?.role === 'ADMIN';

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-lg font-semibold text-slate-900">Suggestions</h1>
        <p className="text-sm text-slate-500">
          Ideas to improve the ticketing system. Anyone can submit; you can edit or delete your own.
          {isAdmin ? ' As an admin, you can also move status and edit/delete any.' : ''}
        </p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <section className="space-y-3 bg-white border border-slate-200 rounded-md p-3">
        <h2 className="text-sm font-semibold text-slate-700">New suggestion</h2>
        <input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Short title — what's the idea?"
          maxLength={300}
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
        />
        <textarea
          value={newBody}
          onChange={(e) => setNewBody(e.target.value)}
          placeholder="More details — what problem does this solve, what would it look like?"
          rows={4}
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
        />
        <div className="flex justify-end">
          <button
            onClick={() => void create()}
            disabled={creating || !newTitle.trim() || !newBody.trim()}
            className="px-3 py-2 rounded-md bg-brand-600 text-white text-sm hover:bg-brand-700 disabled:opacity-60"
          >
            {creating ? 'Submitting…' : 'Submit suggestion'}
          </button>
        </div>
      </section>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500">No suggestions yet — be the first.</p>
      ) : (
        <ul className="space-y-3">
          {items.map((s) => (
            <SuggestionCard
              key={s.id}
              s={s}
              me={me}
              isAdmin={isAdmin}
              onChange={load}
              onError={setError}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

interface CardProps {
  s: SuggestionRow;
  me: UserSummary | null;
  isAdmin: boolean;
  onChange: () => Promise<void>;
  onError: (msg: string) => void;
}

function SuggestionCard({ s, me, isAdmin, onChange, onError }: CardProps) {
  const isAuthor = me?.id === s.authorUserId;
  const canEdit = isAdmin || isAuthor;

  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(s.title);
  const [body, setBody] = useState(s.body);
  const [saving, setSaving] = useState(false);

  // Local "expanded" admin row (status + note).
  const [adminNote, setAdminNote] = useState(s.adminNote ?? '');

  async function saveEdit() {
    if (saving) return;
    setSaving(true);
    try {
      await Suggestions.update(s.id, { title: title.trim(), body: body.trim() });
      setEditing(false);
      await onChange();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function changeStatus(next: SuggestionStatus) {
    try {
      await Suggestions.update(s.id, { status: next });
      await onChange();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Status change failed');
    }
  }

  async function saveAdminNote() {
    try {
      await Suggestions.update(s.id, { adminNote: adminNote.trim() || null });
      await onChange();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Save failed');
    }
  }

  async function remove() {
    const ok = await confirmDialog({
      title: 'Delete suggestion?',
      body: `"${s.title}" will be permanently deleted. This cannot be undone.`,
      tone: 'danger',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await Suggestions.remove(s.id);
      await onChange();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  return (
    <li className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {editing ? (
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={300}
              className="w-full px-2 py-1 border border-slate-300 rounded text-sm font-medium"
            />
          ) : (
            <h3 className="font-medium text-slate-900">{s.title}</h3>
          )}
          <div className="text-xs text-slate-500 mt-1">
            {s.author.name} · {new Date(s.createdAt).toLocaleDateString()}
          </div>
        </div>
        <Badge tone={suggestionStatusTone[s.status]} className="shrink-0">
          {suggestionStatusLabel[s.status]}
        </Badge>
      </div>

      {editing ? (
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
        />
      ) : (
        <p className="text-sm text-slate-700 whitespace-pre-wrap">{s.body}</p>
      )}

      {s.adminNote && !editing && (
        <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2 text-slate-700">
          <span className="font-medium text-slate-800">Admin note:</span> {s.adminNote}
        </div>
      )}

      {isAdmin && !editing && (
        <div className="border-t border-slate-100 pt-3 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs text-slate-600">Status:</label>
            <select
              value={s.status}
              onChange={(e) => void changeStatus(e.target.value as SuggestionStatus)}
              className="px-2 py-1 border border-slate-300 rounded text-xs"
            >
              {STATUS_OPTIONS.map((st) => (
                <option key={st} value={st}>{suggestionStatusLabel[st]}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <input
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="Admin note (optional, visible to all)"
              className="flex-1 px-2 py-1 border border-slate-300 rounded text-xs"
            />
            <button
              onClick={() => void saveAdminNote()}
              className="px-2 py-1 text-xs rounded bg-slate-200 hover:bg-slate-300 text-slate-700"
            >
              Save note
            </button>
          </div>
        </div>
      )}

      {canEdit && (
        <div className="flex gap-3 text-xs border-t border-slate-100 pt-3">
          {editing ? (
            <>
              <button
                onClick={() => void saveEdit()}
                disabled={saving || !title.trim() || !body.trim()}
                className="px-2 py-1 rounded bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setTitle(s.title);
                  setBody(s.body);
                }}
                className="text-slate-500 hover:text-slate-700"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setEditing(true)}
                className="text-brand-600 hover:text-brand-700"
              >
                Edit
              </button>
              <button
                onClick={() => void remove()}
                className="text-red-600 hover:text-red-700"
              >
                Delete
              </button>
            </>
          )}
        </div>
      )}
    </li>
  );
}
